import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Check, X, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/testimonials`);
      setRows(data);
    } catch (e) {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id, approved) => {
    try {
      const { data } = await axios.patch(`${API}/admin/testimonials/${id}/approve?approved=${approved}`);
      setRows((r) => r.map((x) => (x.id === id ? data : x)));
      toast.success(approved ? "Published" : "Unpublished");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const filtered = rows.filter((r) => (filter === "pending" ? !r.approved : filter === "published" ? r.approved : true));
  const pending = rows.filter((r) => !r.approved).length;

  const btn = (id, label) => (
    <button
      key={id}
      onClick={() => setFilter(id)}
      data-testid={`testimonial-filter-${id}`}
      className={`rounded-full border-2 border-brand-ink px-3 py-1 text-sm font-bold ${filter === id ? "bg-brand-red text-white" : "bg-white text-brand-ink hover:bg-brand-yellow/60"}`}
    >{label}</button>
  );

  return (
    <div data-testid="testimonials-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold text-brand-ink">Testimonials</h2>
          <p className="text-brand-ink/70">{rows.length} total · {pending} pending review</p>
        </div>
        <div className="flex gap-2">
          {btn("all", "All")}
          {btn("pending", `Pending (${pending})`)}
          {btn("published", "Published")}
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {loading && <p className="p-4 text-brand-ink/60">Loading…</p>}
        {!loading && filtered.length === 0 && (
          <p className="p-8 text-center text-brand-ink/60 md:col-span-2">Nothing here.</p>
        )}
        {filtered.map((t) => (
          <div key={t.id} className="rounded-3xl border-[3px] border-brand-ink bg-white p-5 shadow-brutal-sm relative" data-testid={`testimonial-card-${t.id}`}>
            <Quote size={28} className="text-brand-yellow absolute -top-4 left-4" fill="#FFD166" />
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 rounded-full border-2 border-brand-ink px-2 py-0.5 text-xs font-bold ${t.approved ? "bg-brand-green text-white" : "bg-brand-yellow text-brand-ink"}`}>
                {t.approved ? "Published" : "Pending"}
              </span>
              <span className="text-xs text-brand-ink/50">{new Date(t.created_at).toLocaleString()}</span>
            </div>
            <p className="text-brand-ink/85 leading-relaxed">“{t.message}”</p>
            <p className="mt-3 font-bold text-brand-red">{t.name}</p>
            <p className="text-brand-ink/70 text-sm">{t.relation}</p>

            <div className="mt-4 flex gap-2">
              {!t.approved ? (
                <button
                  onClick={() => approve(t.id, true)}
                  data-testid={`testimonial-approve-${t.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-green text-white font-bold px-3 py-1.5 text-sm border-2 border-brand-ink hover:-translate-y-0.5 transition-transform"
                >
                  <Check size={14} /> Approve
                </button>
              ) : (
                <button
                  onClick={() => approve(t.id, false)}
                  data-testid={`testimonial-unpublish-${t.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white text-brand-ink font-bold px-3 py-1.5 text-sm border-2 border-brand-ink hover:bg-brand-yellow/60"
                >
                  <X size={14} /> Unpublish
                </button>
              )}
              <button
                onClick={() => remove(t.id)}
                data-testid={`testimonial-delete-${t.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-white text-brand-red font-bold px-3 py-1.5 text-sm border-2 border-brand-ink hover:bg-brand-red hover:text-white transition-colors"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
